import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Card } from '../ui/card';

// Fisher-Yates shuffle (returns a new array)
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const Categorize = ({ question, answer, onAnswer, showResult }) => {
  const rawItems = question.items_to_categorize || [];
  const categories = question.categories || [];

  // Shuffle once on mount (or when question changes)
  const [shuffledItems] = useState(() => shuffleArray(rawItems));

  // Track answers: { [item.text]: chosenCategory | null }
  const [itemPositions, setItemPositions] = useState(() => {
    if (answer) return answer;
    const positions = {};
    rawItems.forEach(item => { positions[item.text] = null; });
    return positions;
  });

  // Index of the current item being shown (one-at-a-time mode)
  const [currentIndex, setCurrentIndex] = useState(0);

  // Whether the user has finished all items
  const allDone = currentIndex >= shuffledItems.length;

  // Sync answers up to parent whenever itemPositions changes
  const prevPositionsRef = useRef(itemPositions);
  useEffect(() => {
    if (prevPositionsRef.current !== itemPositions) {
      prevPositionsRef.current = itemPositions;
      onAnswer(itemPositions);
    }
  }, [itemPositions, onAnswer]);

  const handleChoose = (category) => {
    if (showResult) return;
    document.activeElement?.blur();
    const item = shuffledItems[currentIndex];
    setItemPositions(prev => ({ ...prev, [item.text]: category }));
    setCurrentIndex(prev => prev + 1);
  };

  const isItemCorrect = (item) => itemPositions[item.text] === item.category;

  if (rawItems.length === 0) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
        Este exercício de categorização não tem itens configurados ainda.
      </div>
    );
  }

  // ── Result view (after all items categorized, or showResult from outside) ──
  if (showResult || allDone) {
    const total = shuffledItems.length;
    const correct = shuffledItems.filter(i => isItemCorrect(i)).length;

    return (
      <div className="space-y-6">
        <div className="bg-purple-50 p-6 rounded-xl">
          <p className="text-lg text-gray-900">{question.question}</p>
          <p className="text-sm text-gray-600 mt-2">Resultado:</p>
        </div>

        {/* Score summary */}
        <div className={`text-center p-4 rounded-xl font-semibold text-lg ${
          correct === total ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {correct}/{total} corretos
        </div>

        {/* Per-item breakdown */}
        <div className="space-y-2">
          {shuffledItems.map(item => {
            const ok = isItemCorrect(item);
            return (
              <div
                key={item.text}
                className={`flex items-center justify-between p-3 rounded-lg border-2 ${
                  ok ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
                }`}
              >
                <div className="flex-1">
                  <span className="text-gray-900 font-medium">{item.text}</span>
                  {!ok && (
                    <p className="text-xs text-red-600 mt-0.5">
                      Sua resposta: <strong>{itemPositions[item.text] || '—'}</strong>
                      {' · '}Correto: <strong>{item.category}</strong>
                    </p>
                  )}
                </div>
                {ok
                  ? <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  : <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                }
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── One-at-a-time interaction view ──
  const currentItem = shuffledItems[currentIndex];
  const progress = currentIndex + 1;

  return (
    <div className="space-y-6">
      {/* Question prompt */}
      <div className="bg-purple-50 p-6 rounded-xl">
        <p className="text-lg text-gray-900">{question.question}</p>
        <p className="text-sm text-gray-600 mt-2">
          Item {progress} de {shuffledItems.length} — escolha a categoria correta
        </p>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-purple-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex) / shuffledItems.length) * 100}%` }}
        />
      </div>

      {/* Current item card */}
      <Card className="p-6 text-center">
        <p className="text-xl font-semibold text-gray-900">{currentItem.text}</p>
      </Card>

      {/* Category buttons */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {categories.map(category => (
          <button
            key={category}
            onClick={() => handleChoose(category)}
            className="w-full py-4 px-6 rounded-xl border-2 border-purple-300 bg-white hover:bg-purple-50 hover:border-purple-500 text-gray-900 font-medium text-base transition-all active:scale-95"
          >
            {category}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Categorize;
